import { EDocumentType, EExternalStepStatus, EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { Chip, TableCell, TableRow } from '@mui/material'
import useFeatureFlags from '@src/hooks/config-hooks'
import { RequiredExternalStep } from '@src/types/CreditApplicationSchema'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { Dispatch, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentNote from './DocumentNote'
import ResendIdentityVerificationButton from './ResendIdentityVerificationButton'
import UploadFileButton from './UploadFileButton'
import {
  canSendOrDeleteHighEstimateInvoice,
  isIdentityVerificationDocument,
  useDocumentStatus,
  useFullNameText,
} from './documentTableRow-hooks'

interface Props {
  documentRequired: RequiredDocument
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  setError: Dispatch<Error>
  canEdit: boolean
  worksheet: FilteredWorksheet | null
  identityVerification: RequiredExternalStep | null
  isMerchantDashboardOrigin: boolean
}

const DocumentTableRow = ({
  documentRequired,
  creditApplicationId,
  financingProgramId,
  setError,
  canEdit,
  worksheet,
  identityVerification,
  isMerchantDashboardOrigin,
}: Props) => {
  const { t } = useTranslation()
  const { flags } = useFeatureFlags()
  const { statusMessage, chipColor } = useDocumentStatus(documentRequired)
  const fullNameText = useFullNameText(documentRequired)

  const isIdentityVerification = isIdentityVerificationDocument(
    documentRequired,
    identityVerification,
    !!flags.EnableVouchedForThinCreditB2B,
  )

  // The customer proves identity through the verification link, so the merchant resends that link
  // instead of uploading the document. Nothing to resend once verification has completed, and the
  // endpoint only accepts applications originating in the dashboard, matching the other mutations.
  const showResendIdentityVerificationButton =
    isIdentityVerification &&
    canEdit &&
    isMerchantDashboardOrigin &&
    identityVerification?.status !== EExternalStepStatus.Completed

  const showUploadButton = useMemo(() => {
    if (isIdentityVerification) {
      return false
    }
    if (canEdit && documentRequired.typeId !== EDocumentType.ClinicInvoice) {
      return true
    }
    if (
      worksheet?.isHighEstimate &&
      worksheet.firstPaymentOn &&
      canEdit &&
      documentRequired.typeId === EDocumentType.ClinicInvoice &&
      canSendOrDeleteHighEstimateInvoice(new Date(worksheet.firstPaymentOn))
    ) {
      return true
    }
    return false
  }, [isIdentityVerification, canEdit, documentRequired.typeId, worksheet?.isHighEstimate, worksheet?.firstPaymentOn])

  return (
    <TableRow>
      <TableCell style={{ borderBottom: 'none', fontWeight: 'bold' }}>{fullNameText}</TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.requiredFrom && t(`ERequiredFrom.${documentRequired.requiredFrom}`, { ns: 'enum' })}
      </TableCell>

      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.requiredBeforeStep &&
          t(`ECreditApplicationStep.${documentRequired.requiredBeforeStep}`, { ns: 'enum' })}
      </TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {formatDate(documentRequired.createdOn)}
      </TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.receivedOn && formatDate(documentRequired.receivedOn)}
      </TableCell>
      <TableCell
        style={{
          borderBottom: 'none',
          textAlign: 'center',
        }}
      >
        <Chip
          color={chipColor}
          style={{
            fontWeight: 'bold',
          }}
          label={statusMessage}
        />
      </TableCell>
      <TableCell sx={{ maxWidth: '18rem', borderBottom: 'none' }}>
        <DocumentNote message={documentRequired.note} />
        <DocumentNote message={documentRequired.refusalReason} color={`${chipColor}.main`} />
      </TableCell>
      {showUploadButton && (
        <TableCell sx={{ borderBottom: 'none', textAlign: 'center' }}>
          <UploadFileButton
            applicantType={documentRequired.applicantType}
            financingProgramId={financingProgramId}
            typeId={documentRequired.typeId}
            subKey={documentRequired.subKey}
            status={documentRequired.status}
            creditApplicationId={creditApplicationId}
            setError={setError}
          />
        </TableCell>
      )}
      {showResendIdentityVerificationButton && (
        <TableCell sx={{ borderBottom: 'none', textAlign: 'center' }}>
          <ResendIdentityVerificationButton
            applicantType={documentRequired.applicantType}
            financingProgramId={financingProgramId}
            creditApplicationId={creditApplicationId}
            setError={setError}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

export default DocumentTableRow
